import { Component } from 'react';
import { connect } from 'react-redux';

import Button from 'components/atoms/Button';
import DropList from 'components/atoms/DropList';
import Form from 'components/atoms/Form';
import FormGroup from 'components/atoms/FormGroup';
import Icon from 'components/atoms/Icon';
import Input from 'components/atoms/Input';
import Label from 'components/atoms/Label';
import ToggleButton from 'components/atoms/ToggleButton';
import { ADMIN_TAG_TYPES, TAG_SHARED_TYPES, TAG_TYPES } from 'data/static';
import { bindActionCreators, Dispatch } from 'redux';
import { tagUpdate } from 'services/tags';
import { SetErrorHandlerAction, setErrorHandlerAction } from 'store/actions/errorsActions';
import { PortType, RootState, UserType } from 'types';
import { MainControlsInterface } from 'types/functions';
import { ActionsType } from 'types/system';
import { renderError, setKey } from 'utils/formHandlers';
import { formatServerErrors } from 'utils/formatServerErrors';
import { userTypeForPort } from 'utils/permission';
import { translate } from 'utils/translate';
import validation from 'utils/validation';

interface EditTagModelProps {
    //unused props
    children?: string | JSX.Element | JSX.Element[];

    //unused props
    language: string;

    id?: string;
    data: any;
    actions: ActionsType | undefined;
    port: PortType;
    mainControls: MainControlsInterface;
    setErrorHandlerAction: SetErrorHandlerAction;
    user: UserType;
}

interface EditTagModelState {
    errors: any;

    //unused state
    data: any | null;

    loading: boolean;
    tag: {
        id: number;
        tag: string;
        label: string;
        port_id: number;
        order: 0;
        autoApply: boolean;
        type: string;
    };
}

class EditTagModel extends Component<EditTagModelProps, EditTagModelState> {
    constructor(props: EditTagModelProps) {
        super(props);
        this.state = {
            data: null,
            loading: false,
            errors: {},
            //fix autoApply if it's string
            tag: {
                ...props.data.tag,
                autoApply: props.data.tag.autoApply === 'true' || props.data.tag.autoApply === true,
            },
        };
    }

    handleChange = (name: string, value: unknown) => {
        const { tag, errors } = this.state;
        setKey(tag, name, value);
        setKey(errors, name, null);
        this.setState({ tag, errors });
    };

    handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e?.preventDefault();
        this.setState({ errors: {}, loading: true }, () => {
            const validationClass = new validation([{ name: 'tag', rules: ['required'] }]);
            const { errors, status } = validationClass.check(this.state.tag);
            if (status) {
                this.setState({ errors, loading: false });
                return;
            }

            tagUpdate(this.props.port.id, this.state.tag?.id, {
                ...this.state.tag,
                autoApply: this.state.tag.autoApply.toString(),
            })
                .then(() => {
                    this.props.mainControls.refreshTaglist();
                    this.props.actions?.onConfirm();
                    this.props.actions?.onCancel();
                })
                .catch((errors) => {
                    switch (errors.response?.status) {
                        case 422:
                            this.setState({ errors: formatServerErrors(errors), loading: false });
                            break;
                        default:
                            this.setState({ loading: false });
                            this.props.setErrorHandlerAction(errors);
                            break;
                    }

                    return;
                });
        });
    };

    handleFormCancel = () => {
        this.props.actions?.onCancel();
    };

    render() {
        return (
            <>
                <Form onSubmit={this.handleFormSubmit} className="bg-white shadow p-4 w-full rounded">
                    <div id={`Drag-${this.props.id}`} className="flex justify-between mb-2 no-select ">
                        <div className="text-lg leading-6 font-bold">{translate('Edit tag')}</div>
                        <div
                            onTouchEnd={this.handleFormCancel}
                            onClick={this.handleFormCancel}
                            className="cursor-pointer"
                        >
                            <Icon>
                                <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                <path d="M18 6l-12 12" />
                                <path d="M6 6l12 12" />
                            </Icon>
                        </div>
                    </div>
                    <FormGroup label={translate('Tag')} comments={renderError(this.state.errors?.tag)} required={true}>
                        <Input
                            // disabled={
                            //     tagTypesPrivate.indexOf(this.state.tag.type) >= 0 || this.state.tag.port_id == null
                            // }
                            name="tag"
                            value={this.state?.tag.tag}
                            onChange={this.handleChange}
                        />
                    </FormGroup>
                    <FormGroup
                        label={translate('Label')}
                        comments={renderError(this.state.errors.label)}
                        required={false}
                    >
                        <Input name="label" value={this.state?.tag.label} onChange={this.handleChange} />
                    </FormGroup>
                    <FormGroup
                        label={translate('Order')}
                        comments={renderError(this.state.errors.order)}
                        required={false}
                    >
                        <Input type="number" name="order" value={this.state?.tag.order} onChange={this.handleChange} />
                    </FormGroup>
                    <FormGroup comments={renderError(this.state.errors?.type)} required={false}>
                        <Label>{translate('Type')}</Label>
                        <DropList
                            capitalize
                            name="type"
                            id="type"
                            value={this.state.tag?.type}
                            options={
                                this.state.tag?.type === 'public' || this.state.tag?.type === 'private'
                                    ? TAG_SHARED_TYPES
                                    : userTypeForPort(this.props.user, this.props.port.id) === 'systemAdministrator'
                                    ? ADMIN_TAG_TYPES
                                    : TAG_TYPES
                            }
                            onChange={this.handleChange}
                        ></DropList>
                    </FormGroup>

                    {(this.state?.tag.type === 'public' || this.state?.tag.type === 'private') && (
                        <div className="mt-5 flex items-center gap-1">
                            <div className="flex items-center align-middle">
                                <ToggleButton
                                    onChange={this.handleChange}
                                    name="autoApply"
                                    value={this.state.tag.autoApply}
                                ></ToggleButton>
                            </div>
                            <div>{translate('Apply this user tag to all new transits')}</div>
                        </div>
                    )}
                    <div className="flex justify-end">
                        <div className="mt-5 flex gap-3">
                            <Button
                                type="button"
                                className="px-4 py-2 font-bold border border-gray-400"
                                onClick={this.handleFormCancel}
                            >
                                {translate('Cancel')}
                            </Button>
                            <Button
                                disabled={this.state.loading}
                                loading={this.state.loading}
                                type="submit"
                                className="px-4 py-2 bg-primary-submit rounded-md text-white"
                            >
                                {translate('Save')}
                            </Button>
                        </div>
                    </div>
                </Form>
            </>
        );
    }
}
const editTagStateToProps = (state: RootState) => ({
    language: state.user.user.language,
    mainControls: state.layout.mainControls,
    port: state.port.port,
    user: state.user.user,
});

const editTagDispatchToProps = (dispatch: Dispatch) => ({
    setErrorHandlerAction: bindActionCreators(setErrorHandlerAction, dispatch),
});

export default connect(editTagStateToProps, editTagDispatchToProps)(EditTagModel);
